/**
 * NativePlaybackEngine
 *
 * Thin JS wrapper around the native Capacitor audio plugin (MixGoatAudio).
 * Plugin source:
 *   iOS:     ios-app/App/App/plugins/MixGoatAudioPlugin.swift  (AVPlayer)
 *   Android: android-app/.../MixGoatAudioPlugin.kt             (ExoPlayer)
 *
 * Architecture (Phase 1 of native-as-source-of-truth):
 *   - The native plugin OWNS the queue, the position, the auto-advance.
 *   - JS hands the plugin the whole queue + startIndex via `setQueue`, then
 *     gets back a single unified `playerState` event stream (4Hz while
 *     playing + transitions + resume snapshot) that JS forwards into Redux.
 *   - jinke / HTML5 `<audio>` is GONE on native (see MobilePlayer.jsx).
 *
 * Event subscription: callers register listeners; the plugin posts Capacitor
 * events that we re-dispatch to subscribers.
 */

class NativePlaybackEngine {
  constructor() {
    // Plugin lookup is LAZY because Capacitor's plugin registration happens
    // via MixGoatBridgeViewController.capacitorDidLoad(), which fires AFTER
    // the JS shim evaluates and even after React mounts. Eager lookup at
    // construction returns null and freezes the engine as "unavailable"
    // forever, even though the plugin DOES register a moment later.
    this.plugin = null;
    this.listenersAttached = false;
    this.listeners = new Map(); // event name → Set<fn>
    // Highest playerState `seq` we've propagated. During lock-screen
    // backgrounding, Capacitor buffers playerState events; on resume
    // they arrive in order but the LAST few overwrite Redux with
    // stale mid-lock state. We drop any incoming event whose seq is
    // <= the latest seen so the appStateChange-driven getState()
    // snapshot (which carries the freshest seq) wins.
    this._lastPlayerSeq = -1;
    this._ensurePlugin();
  }

  _ensurePlugin() {
    if (this.plugin) return this.plugin;
    if (typeof window === 'undefined' || !window.electron) return null;
    const candidate = window.electron.nativeAudio;
    if (!candidate) return null;
    this.plugin = candidate;
    if (!this.listenersAttached) {
      this._attachPluginListeners();
      this.listenersAttached = true;
    }
    return this.plugin;
  }

  isAvailable() {
    return !!this._ensurePlugin();
  }

  /**
   * Pre-buffer likely-to-be-tapped sample URLs natively (top of the For
   * You feed). The plugin opens the connection and buffers the first
   * seconds of each sample in a muted paused player, so the session's
   * first real play starts from warm buffer instead of paying cold
   * DNS + TLS + buffering. No-op when the plugin (or the method — not
   * yet on Android) is unavailable.
   */
  async preload(urls) {
    const plugin = this._ensurePlugin();
    if (!plugin || typeof plugin.preload !== 'function') return;
    const clean = (urls || []).filter(u => typeof u === 'string' && u);
    if (!clean.length) return;
    try { await plugin.preload({ urls: clean }); } catch (_) { /* fire-and-forget */ }
  }

  /**
   * Pre-warm the native audio session at app mount so the first user-
   * initiated play doesn't block on synchronous setCategory + setActive.
   * Safe to call repeatedly; the native side is idempotent.
   */
  async prepare() {
    const plugin = this._ensurePlugin();
    if (!plugin) return;
    try {
      if (typeof plugin.prepare === 'function') {
        await plugin.prepare();
      }
    } catch (_) { /* fire-and-forget */ }
  }

  _attachPluginListeners() {
    // The native plugin emits exactly these events:
    //   'playerState'  { index, positionSec, duration, isPlaying, title,
    //                    artist, artworkUrl, queueLength }
    //                  — 4Hz while playing + every transition + once on
    //                    appResume / didBecomeActive (resume snapshot).
    //                    This is the ONLY state event JS needs to track —
    //                    no separate playbackState / timeUpdate / trackAdvanced.
    //   'remoteCommand' { action: 'play'|'pause'|'next'|'prev' }
    //                  — lock-screen / Bluetooth / Control Center buttons.
    //                    Native already advances on next/prev — these are
    //                    informational so the JS UI can react if needed.
    //   'error'        { code, message }
    const events = ['playerState', 'remoteCommand', 'error'];
    events.forEach((ev) => {
      try {
        this.plugin.addListener(ev, (data) => {
          // Monotonic-seq filter for playerState: discard buffered
          // events delivered out-of-order after a long lock. The
          // freshest snapshot wins regardless of how many stale
          // events were queued behind it.
          if (ev === 'playerState' && data && typeof data.seq === 'number') {
            if (data.seq <= this._lastPlayerSeq) return;
            this._lastPlayerSeq = data.seq;
          }
          this._emit(ev, data);
        });
      } catch (err) {
        console.warn('[NativePlaybackEngine] failed to attach', ev, err);
      }
    });
  }

  on(event, fn) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event).add(fn);
    return () => this.off(event, fn);
  }

  off(event, fn) {
    const set = this.listeners.get(event);
    if (set) set.delete(fn);
  }

  _emit(event, data) {
    const set = this.listeners.get(event);
    if (set) for (const fn of set) {
      try { fn(data); } catch (err) { console.error('[NativePlaybackEngine] listener threw', err); }
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Queue + transport — these all map 1:1 to native plugin methods.
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Replace the native queue with `items` and load `items[startIndex]`.
   * Does NOT auto-play; caller picks play(). The native side does ALL
   * subsequent auto-advances internally (lock-screen safe).
   * @param {object} params
   * @param {Array<{url, title, artist, artworkUrl}>} params.items
   * @param {number} params.startIndex
   */
  async setQueue({ items, startIndex }) {
    if (!this.plugin) return { error: 'Native audio plugin not available' };
    try {
      return await this.plugin.setQueue({
        items: (items || []).map(t => ({
          url: t.url || t.musicSrc || '',
          title: t.title || t.name || '',
          artist: t.artist || t.singer || '',
          artworkUrl: t.artworkUrl || t.cover || '',
        })),
        startIndex: Math.max(0, startIndex | 0),
      });
    } catch (err) {
      return { error: err?.message || 'setQueue failed' };
    }
  }

  async playIndex(index) {
    if (!this.plugin) return;
    try { await this.plugin.playIndex({ index: index | 0 }); } catch (_) {}
  }

  async play() {
    if (!this.plugin) return;
    try { await this.plugin.play(); } catch (_) {}
  }

  async pause() {
    if (!this.plugin) return;
    try { await this.plugin.pause(); } catch (_) {}
  }

  async nextTrack() {
    if (!this.plugin) return;
    try { await this.plugin.nextTrack(); } catch (_) {}
  }

  async prevTrack() {
    if (!this.plugin) return;
    try { await this.plugin.prevTrack(); } catch (_) {}
  }

  async stop() {
    if (!this.plugin) return;
    try { await this.plugin.stop(); } catch (_) {}
  }

  async seek(seconds) {
    if (!this.plugin) return;
    try { await this.plugin.seek({ position: Number(seconds) || 0 }); } catch (_) {}
  }

  async setVolume(v) {
    if (!this.plugin) return;
    const clamped = Math.max(0, Math.min(1, Number(v) || 0));
    try { await this.plugin.setVolume({ volume: clamped }); } catch (_) {}
  }

  async setRate(rate) {
    if (!this.plugin) return;
    try { await this.plugin.setRate({ rate: Number(rate) || 1 }); } catch (_) {}
  }

  /**
   * Snapshot of current native player state. Mostly used as belt-and-
   * suspenders behind the resume `playerState` emit — JS reads this on
   * appStateChange:isActive to reconcile if the buffered event was dropped.
   * Returns { index, positionSec, duration, isPlaying, title, artist,
   * artworkUrl, queueLength } or null.
   */
  async getState() {
    if (!this.plugin || typeof this.plugin.getState !== 'function') return null;
    try {
      const snap = await this.plugin.getState();
      // Bump _lastPlayerSeq so any older buffered playerState events
      // arriving after this snapshot are discarded. Without this, a
      // resume-from-lock snapshot would be immediately overwritten by
      // the trailing buffered events that Capacitor queued during
      // the lock window.
      if (snap && typeof snap.seq === 'number' && snap.seq > this._lastPlayerSeq) {
        this._lastPlayerSeq = snap.seq;
      }
      return snap;
    } catch (_) { return null; }
  }

  /**
   * Push the lock-screen refill context to the native plugin. While the
   * WebView is suspended (screen locked), the plugin uses this to extend
   * its own queue: items from `listContextItems` first, then recs HTTP as
   * fallback. JS calls this whenever the relevant Redux fields change.
   * No-op when the plugin isn't available.
   */
  async setRefillContext(params) {
    if (!this.plugin || typeof this.plugin.setRefillContext !== 'function') return;
    try { await this.plugin.setRefillContext(params); } catch (_) {}
  }

  /**
   * Drive the lock-screen heart. `liked` fills it; `enabled` controls
   * whether it is offered at all — JS passes false whenever it cannot
   * identify the playing track, so the user is never shown a heart that
   * would like the wrong thing. No-ops on a plugin build without the
   * method (the `.m` allowlist and the Swift pluginMethods array must BOTH
   * list it, or this silently does nothing).
   */
  async setLikeState({ liked, enabled }) {
    const plugin = this._ensurePlugin();
    if (!plugin || typeof plugin.setLikeState !== 'function') return;
    try {
      await plugin.setLikeState({ liked: !!liked, enabled: enabled !== false });
    } catch (_) { /* cosmetic — never break playback over it */ }
  }

  /**
   * Light-weight token rotation when JS has refreshed the JWT but the
   * rest of the refill context is unchanged.
   */
  async updateRefillTokens({ accessToken, refreshToken }) {
    if (!this.plugin || typeof this.plugin.updateRefillTokens !== 'function') return;
    try { await this.plugin.updateRefillTokens({ accessToken: accessToken || '', refreshToken: refreshToken || '' }); } catch (_) {}
  }

  dispose() {
    this.listeners.clear();
    if (this.plugin && this.plugin.removeAllListeners) {
      try { this.plugin.removeAllListeners(); } catch (_) {}
    }
  }
}

// Singleton — there's exactly one native player at any time.
let _instance = null;
export function getNativePlaybackEngine() {
  if (!_instance) _instance = new NativePlaybackEngine();
  return _instance;
}

export default NativePlaybackEngine;
